"use client";

import { Box, Skeleton, Stack, Divider } from "@mui/material";
import { SurfaceCard } from "@/components/owner-admin/surface-card";
import { DashboardSurface } from "@/components/owner-admin/ui-surfaces";

export function OwnerShellSkeleton() {
  const sidebarWidth = 280;

  return (
    <Box className="min-h-screen bg-[var(--tc-bg)]">
      <Box className="flex w-full">
        {/* Desktop Sidebar Skeleton */}
        <Box
          className="hidden h-screen shrink-0 overflow-hidden lg:block sticky top-0"
          sx={{
            width: sidebarWidth,
            backgroundColor: "var(--tc-sidebar-bg)",
            borderRight: "1px solid var(--tc-divider)",
          }}
        >
          <Stack spacing={1} className="h-full">
            <Box className="px-4 py-6" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Skeleton variant="text" width={80} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                <Skeleton variant="text" width={120} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Box>
              <Skeleton variant="circular" width={28} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            </Box>
            
            <Box className="flex-1 overflow-y-auto overflow-x-hidden px-3">
              {[1, 2].map((section) => (
                <Box key={section} className="mb-4">
                  <Skeleton variant="text" width={100} height={20} sx={{ ml: 2, mb: 1, bgcolor: 'rgba(255,255,255,0.1)' }} />
                  {[1, 2, 3, 4].map((item) => (
                    <Skeleton key={item} variant="rounded" height={44} sx={{ mb: 0.5, borderRadius: 'var(--tc-radius-md)', bgcolor: 'rgba(255,255,255,0.05)' }} />
                  ))}
                </Box>
              ))}
            </Box>

            <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mx: 2 }} />
            
            <Box className="p-4">
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                <Box className="min-w-0 flex-1">
                  <Skeleton variant="text" width={100} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                  <Skeleton variant="text" width={140} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Box className="min-w-0 flex-1">
          <Stack spacing={0}>
            {/* Header Skeleton */}
            <SurfaceCard 
              className="sticky top-0 z-20" 
              sx={{ 
                p: "12px 24px",
                borderRadius: 0,
                borderLeft: "none",
                borderTop: "none",
                borderRight: "none",
                backgroundColor: "var(--tc-surface)",
                opacity: 0.98,
                backdropFilter: "blur(12px)",
                borderBottom: "1px solid var(--tc-border-soft)",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{ 
                  alignItems: "center", 
                  justifyContent: "space-between",
                  px: { xs: 2, md: 4, lg: 6 },
                }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Skeleton variant="text" width={60} height={24} />
                  <Skeleton variant="text" width={10} height={24} />
                  <Skeleton variant="text" width={100} height={24} />
                </Stack>
                <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: 'center', borderColor: 'var(--tc-border-soft)' }} />
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="circular" width={28} height={28} />
                </Stack>
              </Stack>
            </SurfaceCard>

            {/* Main Content Placeholder */}
            <Box className="p-4 md:p-8 lg:p-12 xl:p-16">
              <Box className="mx-auto w-full max-w-[1440px]">
                <DashboardSurface sx={{ gap: { xs: 4, md: 6 } }}>
                   <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: "var(--tc-radius-xl)" }} />
                </DashboardSurface>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
